import { useMemo } from "react";
import { AppDialog } from "../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../common/components/button/defaultButton/DefaultButton";
import { IAnnouncement } from "./models/announcementModel";
import { prepareAnnouncementHtml } from "./utilities/announcementContentUtility";
import styles from "./AnnouncementDialog.module.scss";

const TITLE_ID = "announcement-dialog-title";

interface IAnnouncementDialogProps {
  /** The announcement on screen; null while there is none (the dialog is then closed). */
  announcement: IAnnouncement | null;
  visible: boolean;
  /** 1-based place of this announcement among those waiting, shown when there are several. */
  position?: number;
  total?: number;
  /** "Окей, разбрах": the user has read it. */
  onConfirm: () => void;
  /** "Покажи ми по-късно", Escape or a click outside: close it, ask again next time the app opens. */
  onLater: () => void;
}

/**
 * An announcement a global admin published, shown when the app opens: a title and the rich
 * content (text, screenshots, GIFs) in a scrollable area, answered with its two buttons that
 * float at the bottom of that area.
 * Closing it any other way counts as "later". See useAnnouncements for the queue.
 */
export const AnnouncementDialog = ({
  announcement,
  visible,
  position = 1,
  total = 1,
  onConfirm,
  onLater,
}: IAnnouncementDialogProps) => {
  // Expanded and sanitized before it ever reaches dangerouslySetInnerHTML.
  const html = useMemo(
    () => (announcement ? prepareAnnouncementHtml(announcement.contentHtml) : ""),
    [announcement],
  );

  return (
    // No header, so no close button: Escape and a click outside still close it, as "later".
    <AppDialog
      visible={visible}
      onHide={onLater}
      showHeader={false}
      className={styles.root}
      contentClassName={styles.root_content}
      ariaLabelledBy={TITLE_ID}
    >
      {announcement && (
        // Remounted per announcement, so the next one starts scrolled to the top.
        <div className={styles.root_body} key={announcement.id}>
          <div className={styles.root_header}>
            {total > 1 && (
              <span className={styles.root_counter}>
                {position} от {total}
              </span>
            )}
            <h2 className={styles.root_title} id={TITLE_ID}>
              {announcement.title}
            </h2>
          </div>

          {/* The text and the buttons scroll as one: the buttons stay at the bottom edge and float
              over the text that passes under them. Focusable so the keyboard can scroll it; it is
              also where focus lands on opening. */}
          <div className={styles.root_scroll} role="region" aria-labelledby={TITLE_ID} tabIndex={0}>
            <div className={styles.root_text}>
              <div className={styles.html} dangerouslySetInnerHTML={{ __html: html }} />
            </div>

            {/* "Later" comes first in the markup: side by side it is on the left; stacked on a
                phone (column-reverse) the primary button ends up on top. */}
            <div className={styles.root_actions}>
              <DefaultButton
                className={styles.root_actions_button}
                label="Покажи ми по-късно"
                design="outline"
                width="100%"
                onClick={onLater}
              />
              <DefaultButton
                className={styles.root_actions_button}
                label="Окей, разбрах"
                width="100%"
                onClick={onConfirm}
              />
            </div>
          </div>
        </div>
      )}
    </AppDialog>
  );
};
